import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface ChatMessageBubbleProps {
  role: 'user' | 'assistant';
  children: ReactNode;
  className?: string;
  /**
   * Visual surface for the assistant bubble. Defaults to `'glass'` (the
   * existing `var(--chat-surface)` look). `'white'` switches to the
   * focused-mode treatment from the design handoff: solid
   * `var(--chat-anaka-surface)` background (`#FFFFFF` light / elevated navy
   * dark), `var(--chat-anaka-text)` body text, 14px border-radius — the
   * ThreadAnaka bubble. User bubbles ignore this prop.
   */
  surface?: 'glass' | 'white';
}

/**
 * Message bubble used by the chat thread, per handoff `chat.jsx` /
 * `global-chat.jsx`.
 *
 * - Assistant `surface="white"` (ThreadAnaka): solid
 *   `var(--chat-anaka-surface)` with `var(--chat-anaka-text)`,
 *   `rounded-[14px]`, `12.5px/1.55`, max-width 560px. This is the default
 *   focused-mode treatment from the design handoff; the tokens flip to an
 *   elevated navy in dark mode so the bubble is never white-on-dark.
 * - Assistant `surface="glass"` (detailed mode): `var(--chat-surface)` /
 *   `var(--chat-border)` glass card. Max-width 560px.
 * - User (ThreadUser): right-aligned gradient pill, white text, `rounded-[12px]`,
 *   `12.5px/1.5`, max-width 460px.
 */
export function ChatMessageBubble({
  role,
  children,
  className,
  surface = 'glass',
}: ChatMessageBubbleProps) {
  const isAgent = role === 'assistant';

  if (isAgent) {
    if (surface === 'white') {
      return (
        <div
          data-surface="white"
          className={cn(
            'rounded-[14px] px-[14px] py-[10px] text-[12.5px] leading-[1.55]',
            'max-w-[min(560px,100%)]',
            '[text-wrap:pretty]',
            className,
          )}
          style={{
            background: 'var(--chat-anaka-surface)',
            color: 'var(--chat-anaka-text)',
          }}
        >
          {children}
        </div>
      );
    }
    // glass surface (detailed mode)
    return (
      <div
        data-surface="glass"
        className={cn(
          'rounded-[14px] border px-[14px] py-[10px] text-[12.5px] leading-[1.55] text-foreground backdrop-blur-md',
          'max-w-[min(560px,100%)]',
          '[text-wrap:pretty]',
          className,
        )}
        style={{
          background: 'var(--chat-surface)',
          borderColor: 'var(--chat-border)',
          boxShadow: 'var(--chat-shadow)',
        }}
      >
        {children}
      </div>
    );
  }

  // User bubble — gradient, white text, radius 12, 460px cap
  return (
    <div
      className={cn(
        'rounded-[12px] px-[12px] py-[8px] text-[12.5px] leading-[1.5] text-white',
        'max-w-[min(460px,100%)]',
        className,
      )}
      style={{
        background: 'linear-gradient(135deg, #7C3AED, #3B82F6)',
      }}
    >
      {children}
    </div>
  );
}
